import { buildCapabilityInsight } from "../../../lib/playerReport";
import ReportProvenance from "./ReportProvenance";

const CARD_VIEWBOX = "0 0 220 120";

const ACCENT = "#9aaab8";
const ACCENT_LIGHT = "#c8d3dc";
const ACCENT_DARK = "#6c7885";
const ACCENT_DARKER = "#5f6774";
const TRACK_DARK = "#2f2f34";

function polar(cx, cy, radius, angleDeg) {
  const angle = (angleDeg * Math.PI) / 180;
  return { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) };
}

function arcPath(cx, cy, radius, startAngle, endAngle) {
  const start = polar(cx, cy, radius, startAngle);
  const end = polar(cx, cy, radius, endAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}`;
}

function CapabilityPair({ label, playerName, heading, score, insight, stats, grounding, freshness, children }) {
  return (
    <section className="pr-pair">
      <header className="pr-pair-head">
        <div className="pr-section-head pr-pair-titles">
          <h2>
            {label}
            {playerName && <span className="pr-name"> | {playerName}</span>}
          </h2>
          {heading && <p className="pr-ai-heading">{heading}</p>}
        </div>
        {score != null && <span className="pr-score">{score.toFixed(1)}</span>}
      </header>
      <div className="pr-pair-body">
        <div className="i-card pr-text-card">
          <p className="pr-insight">{insight}</p>
          <dl className="pr-stat-list pr-facts">
            {stats.map((stat) => (
              <div key={stat.label} className="pr-stat-row">
                <dt>{stat.label}</dt>
                <dd>{stat.value}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="i-card pr-chart-card">{children}</div>
      </div>
      <ReportProvenance grounding={grounding} freshness={freshness} />
    </section>
  );
}

function FinishingChart({ data }) {
  const segments = [
    { key: "goals", label: "Goals", value: data.goals, color: ACCENT_LIGHT },
    { key: "onTarget", label: "On target", value: data.onTargetNotGoal, color: ACCENT },
    { key: "offTarget", label: "Off target", value: data.shotsOffTarget, color: ACCENT_DARK },
    { key: "blocked", label: "Blocked", value: data.shotsBlocked, color: TRACK_DARK },
  ].filter((segment) => segment.value > 0);

  const total = segments.reduce((acc, segment) => acc + segment.value, 0) || 1;
  const radius = 38;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <svg viewBox={CARD_VIEWBOX} className="pr-svg" role="img" aria-label="Shot outcomes">
      <g transform="translate(58 58)">
        <circle r={radius} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="15" />
        {segments.map((segment) => {
          const length = (segment.value / total) * circumference;
          const circle = (
            <circle
              key={segment.key}
              r={radius}
              fill="none"
              stroke={segment.color}
              strokeWidth="15"
              strokeDasharray={`${length} ${circumference - length}`}
              strokeDashoffset={-offset}
              transform="rotate(-90)"
            />
          );
          offset += length;
          return circle;
        })}
        <text y="-2" textAnchor="middle" className="pr-hero">
          {data.conversion.toFixed(1)}%
        </text>
        <text y="14" textAnchor="middle" className="pr-hero-sub">
          conversion
        </text>
      </g>
      <g transform="translate(116 22)">
        {segments.map((segment, index) => (
          <g key={segment.key} transform={`translate(0 ${index * 19})`}>
            <rect width="9" height="9" rx="2" fill={segment.color} />
            <text x="15" y="8.5" className="pr-legend">
              {segment.label}
            </text>
            <text x="104" y="8.5" textAnchor="end" className="pr-legend-value">
              {segment.value}
            </text>
          </g>
        ))}
      </g>
    </svg>
  );
}

function ChanceCreationChart({ data }) {
  const segments = [
    { key: "assists", label: "Assists", value: data.assists, color: ACCENT_LIGHT },
    { key: "keyPasses", label: "Key passes", value: data.keyPasses, color: ACCENT },
    { key: "bigChances", label: "Big chances", value: data.bigChancesCreated, color: ACCENT_DARK },
  ];
  const total = segments.reduce((acc, segment) => acc + segment.value, 0) || 1;
  const width = 208;
  let offset = 0;

  return (
    <svg viewBox={CARD_VIEWBOX} className="pr-svg" role="img" aria-label="Chance creation mix">
      <text x="0" y="26" className="pr-hero">
        {data.per90.toFixed(2)}
      </text>
      <text x="0" y="42" className="pr-hero-sub">
        chances created / 90 mins
      </text>
      <text x="208" y="26" textAnchor="end" className="pr-hero-note">
        {data.total} total
      </text>
      <g transform="translate(0 56)">
        {segments.map((segment) => {
          const segmentWidth = (segment.value / total) * width;
          const rect = (
            <rect
              key={segment.key}
              x={offset}
              width={Math.max(0, segmentWidth - 2)}
              height="16"
              rx="4"
              fill={segment.color}
            />
          );
          offset += segmentWidth;
          return rect;
        })}
      </g>
      <g transform="translate(0 92)">
        {segments.map((segment, index) => (
          <g key={segment.key} transform={`translate(${index * 72} 0)`}>
            <text className="pr-legend" y="0">
              {segment.label}
            </text>
            <text className="pr-legend-value" y="15">
              {segment.value}
            </text>
          </g>
        ))}
      </g>
    </svg>
  );
}

function PassingChart({ data }) {
  const accuracy = data.passAccuracy ?? 0;
  const start = 135;
  const sweep = 270;
  const end = start + sweep * Math.min(1, Math.max(0, accuracy / 100));

  return (
    <svg viewBox={CARD_VIEWBOX} className="pr-svg" role="img" aria-label="Pass accuracy">
      <g transform="translate(110 60)">
        <path d={arcPath(0, 0, 44, start, start + sweep)} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="12" strokeLinecap="round" />
        <path d={arcPath(0, 0, 44, start, end)} fill="none" stroke={ACCENT} strokeWidth="12" strokeLinecap="round" />
        <text y="-2" textAnchor="middle" className="pr-hero">
          {accuracy.toFixed(1)}%
        </text>
        <text y="14" textAnchor="middle" className="pr-hero-sub">
          pass accuracy
        </text>
      </g>
      <g transform="translate(0 116)">
        <text className="pr-legend" y="0">
          {data.accuratePasses.toLocaleString()} accurate of {data.passes.toLocaleString()}
        </text>
        <text x="208" y="0" textAnchor="end" className="pr-legend-value">
          {data.passesPer90.toFixed(1)} / 90 mins
        </text>
      </g>
    </svg>
  );
}

function BallControlChart({ data }) {
  const rows = [
    { key: "dispossessed", label: "Dispossessed / 90 mins", value: data.dispossessedPer90, max: 4, target: 0.5, color: ACCENT },
    { key: "dribbledPast", label: "Dribbled past / 90 mins", value: data.dribbledPastPer90, max: 4, target: 0.5, color: ACCENT_DARK },
    { key: "foulsDrawn", label: "Fouls drawn / 90 mins", value: data.foulsDrawnPer90, max: 4, target: 0.5, color: ACCENT_DARKER },
  ];
  const width = 208;

  return (
    <svg viewBox={CARD_VIEWBOX} className="pr-svg" role="img" aria-label="Ball retention">
      {rows.map((row, index) => (
        <g key={row.key} transform={`translate(0 ${16 + index * 34})`}>
          <text className="pr-legend" y="0">
            {row.label}
          </text>
          <text x="208" y="0" textAnchor="end" className="pr-legend-value">
            {row.value.toFixed(2)}
          </text>
          <rect y="7" width={width} height="10" rx="5" fill="rgba(255,255,255,0.07)" />
          <rect
            y="7"
            width={Math.max(3, Math.min(1, row.value / row.max) * width)}
            height="10"
            rx="5"
            fill={row.color}
          />
          <line x1={row.target * width} y1="3" x2={row.target * width} y2="21" stroke="rgba(255,255,255,0.5)" strokeWidth="1.4" strokeDasharray="2 2" />
        </g>
      ))}
      <text x="208" y="116" textAnchor="end" className="pr-hero-note">
        dashed marker = best-in-class target
      </text>
    </svg>
  );
}

function DribblingChart({ data }) {
  const width = 208;
  const rate = Math.min(1, data.attempts > 0 ? data.successful / data.attempts : 0);

  return (
    <svg viewBox={CARD_VIEWBOX} className="pr-svg" role="img" aria-label="Dribble success">
      <text x="0" y="30" className="pr-hero">
        {data.successRate.toFixed(1)}%
      </text>
      <text x="0" y="46" className="pr-hero-sub">
        dribble success rate
      </text>
      <text x="208" y="30" textAnchor="end" className="pr-hero-note">
        {data.per90.toFixed(2)} / 90 mins
      </text>
      <g transform="translate(0 62)">
        <rect width={width} height="20" rx="6" fill="rgba(255,255,255,0.07)" />
        <rect width={Math.max(4, rate * width)} height="20" rx="6" fill={ACCENT} />
        <text x="10" y="14" className="pr-bar-inside">
          {data.successful}
        </text>
        <text x={width - 10} y="14" textAnchor="end" className="pr-bar-inside pr-bar-inside--dim">
          {data.attempts} attempts
        </text>
      </g>
      <text x="0" y="106" className="pr-legend">
        Filled = successful · track = attempts
      </text>
    </svg>
  );
}

function DefendingChart({ data }) {
  const columns = [
    { key: "tackles", label: "Tackles", value: data.tackles },
    { key: "interceptions", label: "Interceptions", value: data.interceptions },
    { key: "clearances", label: "Clearances", value: data.clearances },
  ];
  const max = Math.max(...columns.map((column) => column.value), 1);

  return (
    <svg viewBox={CARD_VIEWBOX} className="pr-svg" role="img" aria-label="Defensive actions">
      {columns.map((column, index) => {
        const height = Math.max(4, (column.value / max) * 62);
        return (
          <g key={column.key} transform={`translate(${14 + index * 68} 0)`}>
            <rect x="0" y={84 - height} width="44" height={height} rx="5" fill={index === 0 ? ACCENT : ACCENT_DARK} />
            <text x="22" y={76 - height} textAnchor="middle" className="pr-column-value">
              {column.value}
            </text>
            <text x="22" y="102" textAnchor="middle" className="pr-column-label">
              {column.label}
            </text>
          </g>
        );
      })}
      <text x="208" y="116" textAnchor="end" className="pr-hero-note">
        {data.per90.toFixed(2)} actions / 90 mins
      </text>
    </svg>
  );
}

function DuelsChart({ data }) {
  const width = 208;
  const wonShare = data.total > 0 ? data.won / data.total : 0;

  return (
    <svg viewBox={CARD_VIEWBOX} className="pr-svg" role="img" aria-label="Duels won">
      <text x="0" y="26" className="pr-hero">
        {data.successRate.toFixed(1)}%
      </text>
      <text x="0" y="42" className="pr-hero-sub">
        duels won
      </text>
      <text x="208" y="26" textAnchor="end" className="pr-hero-note">
        {data.per90.toFixed(1)} duels / 90 mins
      </text>
      <g transform="translate(0 56)">
        <rect width={width} height="22" rx="6" fill="rgba(255,255,255,0.07)" />
        <rect width={Math.max(4, wonShare * width)} height="22" rx="6" fill={ACCENT} />
        <text x="10" y="15" className="pr-bar-inside">
          {data.won} won
        </text>
        <text x={width - 10} y="15" textAnchor="end" className="pr-bar-inside pr-bar-inside--dim">
          {data.lost} lost
        </text>
      </g>
      <text x="0" y="102" className="pr-legend">
        {data.total} duels contested this season
      </text>
    </svg>
  );
}

function AerialChart({ data }) {
  if (!data.available) {
    return (
      <div className="pr-capability-empty">
        No aerial data recorded for this season.
      </div>
    );
  }

  const width = 208;
  const scaleMax = Math.max(2, Math.ceil(data.per90 * 1.4 * 2) / 2);
  const marker = Math.min(1, data.per90 / scaleMax);
  const ticks = [0, scaleMax / 2, scaleMax];

  return (
    <svg viewBox={CARD_VIEWBOX} className="pr-svg" role="img" aria-label="Aerials won">
      <text x="0" y="30" className="pr-hero">
        {data.per90.toFixed(2)}
      </text>
      <text x="0" y="46" className="pr-hero-sub">
        aerials won / 90 mins
      </text>
      <text x="208" y="30" textAnchor="end" className="pr-hero-note">
        {data.won} total
      </text>
      <g transform="translate(0 66)">
        <line x1="0" y1="10" x2={width} y2="10" stroke="rgba(255,255,255,0.16)" strokeWidth="2" />
        {ticks.map((tick) => (
          <g key={tick} transform={`translate(${(tick / scaleMax) * width} 0)`}>
            <line y1="6" y2="14" stroke="rgba(255,255,255,0.25)" strokeWidth="1" />
            <text y="30" textAnchor="middle" className="pr-legend-value">
              {tick.toFixed(1)}
            </text>
          </g>
        ))}
        <circle cx={marker * width} cy="10" r="7" fill={ACCENT} stroke="#171719" strokeWidth="2" />
      </g>
    </svg>
  );
}

function InvolvementChart({ data }) {
  const width = 208;
  const rate = data.appearances > 0 ? Math.min(1, data.lineups / data.appearances) : 0;

  return (
    <svg viewBox={CARD_VIEWBOX} className="pr-svg" role="img" aria-label="Involvement">
      <text x="0" y="30" className="pr-hero">
        {data.minutes.toLocaleString()}
      </text>
      <text x="0" y="46" className="pr-hero-sub">
        minutes played
      </text>
      <text x="208" y="30" textAnchor="end" className="pr-hero-note">
        {data.minutesPerApp.toFixed(1)} min / app
      </text>
      <g transform="translate(0 62)">
        <rect width={width} height="20" rx="6" fill="rgba(255,255,255,0.07)" />
        <rect width={Math.max(4, rate * width)} height="20" rx="6" fill={ACCENT} />
        <text x="10" y="14" className="pr-bar-inside">
          {data.lineups} starts
        </text>
        <text x={width - 10} y="14" textAnchor="end" className="pr-bar-inside pr-bar-inside--dim">
          {data.appearances} apps
        </text>
      </g>
      <text x="0" y="106" className="pr-legend">
        Start share {data.startShare.toFixed(1)}% · {data.bench} bench
      </text>
    </svg>
  );
}

const CAPABILITY_DEFS = [
  { key: "finishing", label: "Finishing", chart: FinishingChart },
  { key: "chanceCreation", label: "Chance Creation", chart: ChanceCreationChart },
  { key: "passing", label: "Passing", chart: PassingChart },
  { key: "ballControl", label: "Ball Control", chart: BallControlChart },
  { key: "dribbling", label: "Dribbling", chart: DribblingChart },
  { key: "defending", label: "Defending", chart: DefendingChart },
  { key: "duels", label: "Duels", chart: DuelsChart },
  { key: "aerial", label: "Aerial", chart: AerialChart },
  { key: "involvement", label: "Involvement", chart: InvolvementChart },
];

/* Chart-only registry so other surfaces (e.g. the Pro chat chart panel) can
   render an individual capability chart without the full report card. */
export const CAPABILITY_CHARTS = CAPABILITY_DEFS.map((definition) => ({
  key: definition.key,
  label: definition.label,
  Chart: definition.chart,
}));

function buildStats(key, data) {
  switch (key) {
    case "finishing":
      return [
        { label: "Goals", value: data.goals },
        { label: "Shots", value: data.shotsTotal },
        { label: "On target", value: `${data.accuracy.toFixed(1)}%` },
      ];
    case "chanceCreation":
      return [
        { label: "Assists", value: data.assists },
        { label: "Key passes", value: data.keyPasses },
        { label: "Big chances", value: data.bigChancesCreated },
      ];
    case "passing":
      return [
        { label: "Passes", value: data.passes.toLocaleString() },
        { label: "Long balls", value: `${data.longBallsWon}/${data.longBalls}` },
        { label: "Through balls", value: `${data.throughBallsWon}/${data.throughBalls}` },
      ];
    case "ballControl":
      return [
        { label: "Dispossessed", value: data.dispossessed },
        { label: "Dribbled past", value: data.dribbledPast },
        { label: "Fouls drawn", value: data.foulsDrawn },
      ];
    case "dribbling":
      return [
        { label: "Attempts", value: data.attempts },
        { label: "Successful", value: data.successful },
        { label: "Per 90", value: data.per90.toFixed(2) },
      ];
    case "defending":
      return [
        { label: "Tackles", value: data.tackles },
        { label: "Interceptions", value: data.interceptions },
        { label: "Clearances", value: data.clearances },
      ];
    case "duels":
      return [
        { label: "Duels", value: data.total },
        { label: "Won", value: data.won },
        { label: "Lost", value: data.lost },
      ];
    case "aerial":
      return [
        { label: "Aerials won", value: data.available ? data.won : "—" },
        { label: "Per 90", value: data.available ? data.per90.toFixed(2) : "—" },
        { label: "Status", value: data.available ? "Recorded" : "Not recorded" },
      ];
    case "involvement":
      return [
        { label: "Minutes", value: data.minutes.toLocaleString() },
        { label: "Appearances", value: data.appearances },
        { label: "Start share", value: `${data.startShare.toFixed(1)}%` },
      ];
    default:
      return [];
  }
}

export default function ReportCapabilityCards({ capabilities, radarAxes = [], playerName, aiCapabilities }) {
  if (!capabilities) return null;
  const scoreByKey = Object.fromEntries(radarAxes.map((axis) => [axis.key, axis.value]));

  return (
    <section className="pr-capability-section">
      <div className="pr-pairs">
        {CAPABILITY_DEFS.map((definition) => {
          const data = capabilities[definition.key];
          if (!data) return null;
          const Chart = definition.chart;
          const stats = buildStats(definition.key, data);
          const ai = aiCapabilities?.[definition.key];
          return (
            <CapabilityPair
              key={definition.key}
              label={definition.label}
              playerName={playerName}
              heading={ai?.heading}
              score={definition.key === "aerial" && !data.available ? null : scoreByKey[definition.key]}
              insight={ai?.insight || buildCapabilityInsight(definition.key, data)}
              stats={stats}
              grounding={ai?.grounding}
              freshness={ai?.freshness}
            >
              <Chart data={data} />
            </CapabilityPair>
          );
        })}
      </div>
    </section>
  );
}
